import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Check, ClipboardCheck, ClipboardList, MessageSquarePlus, X } from 'lucide-react';
import React, { useEffect, useState, useCallback } from 'react';
import { httpClient } from '../../../common/httpclient';
import { standaloneFieldSx } from '../../../common/fieldStyles';
import { usePermissions } from '../../../hooks/usePermissions';
import useIsMobileShell from '../../../hooks/useIsMobileShell';
import MobileChecklistFiller, { renderPhotoBase64 } from './MobileChecklistFiller';
import SaveProgressDialog from './SaveProgressDialog';

/**
 * Center modal — DESIGN-SYSTEM.md §5.6 (scrim + 20px box + header
 * ícone-badge/título/descrição + X + corpo rolável + rodapé com ações à
 * direita), mesmo padrão de `ChecklistSelectionModal.js`/`ChecklistHistoryModal.js`.
 * O corpo reaproveita a anatomia `check-group`/`check-item`/`check-answers` já
 * usada por `Filler.js` (cabeçalho de grupo em gradiente da marca, linha por
 * item com botões de resposta em pílula no tom da resposta, observação
 * opcional). Comportamento (fetch/submit) inalterado.
 */

// Classifica um `Feedback` em ok/nok/manut pela mesma heurística de
// `Filler.js`/`View.js`/`GroupForm.js` (grupos antigos usavam "Atenção" em
// vez de "NOK").
function classifyFeedback(name) {
    const upper = String(name || '').toUpperCase();
    if (upper === 'OK') return 'ok';
    if (upper === 'NOK' || upper.includes('ATEN')) return 'nok';
    if (upper.includes('MANUTEN')) return 'manut';
    return 'other';
}

function answerTone(theme, kind) {
    switch (kind) {
        case 'ok':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'nok':
            return { bg: theme.palette.error.bg, fg: theme.palette.error.main };
        case 'manut':
            return { bg: theme.palette.warning.bg, fg: theme.palette.warning.main };
        default:
            return { bg: theme.palette.info.bg, fg: theme.palette.primary.main };
    }
}

// `.check-answers button` — pílula de resposta. Selecionada ganha o tom da
// resposta (bg + borda + texto); não selecionada é neutra com borda `divider`.
function AnswerButton({ kind, label, selected, onClick }) {
    const theme = useTheme();
    const { bg, fg } = answerTone(theme, kind);
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            sx={{
                fontFamily: 'inherit',
                border: '1.5px solid',
                borderColor: selected ? fg : 'divider',
                backgroundColor: selected ? bg : 'background.paper',
                color: selected ? fg : 'text.secondary',
                borderRadius: '20px',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                padding: '6px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'background-color .12s ease, border-color .12s ease, color .12s ease',
                '&:hover': { borderColor: fg, color: fg },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
        >
            {label}
        </Box>
    );
}

// `.ro-field` — label uppercase micro + valor.
function RoField({ label, value }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary', lineHeight: 1.4, wordBreak: 'break-word' }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

// Botão (ícone) que abre/fecha a observação opcional do item; fica
// destacado quando já existe texto.
function ObservationToggle({ open, hasText, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            title={open ? 'Ocultar observação' : 'Adicionar observação'}
            aria-label={open ? 'Ocultar observação' : 'Adicionar observação'}
            aria-expanded={open}
            sx={(t) => ({
                fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, flexShrink: 0,
                borderRadius: '8px', cursor: 'pointer', p: 0,
                border: '1px solid', borderColor: open || hasText ? 'primary.main' : 'divider',
                backgroundColor: open || hasText ? 'info.bg' : 'background.paper',
                color: open || hasText ? 'primary.main' : 'text.disabled',
                transition: 'background-color .12s ease, border-color .12s ease, color .12s ease',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
            })}
        >
            <MessageSquarePlus size={14} strokeWidth={2.2} />
        </Box>
    );
}

// Linha `.check-item`: nome + botões de resposta + observação opcional.
function CheckItemRow({ item, isLast, feedbackOptions, evaluation, onAnswer, onObservation }) {
    const [obsOpen, setObsOpen] = useState(Boolean(evaluation.observation));

    return (
        <Box sx={{ p: '12px 16px', borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <Typography sx={{ flex: 1, minWidth: 160, fontSize: 13, fontWeight: 600 }}>
                    {item.insp_item_name}
                </Typography>
                <Box role="group" aria-label={`Resposta para ${item.insp_item_name}`} sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flexShrink: 0 }}>
                    {feedbackOptions.map((feedback) => (
                        <AnswerButton
                            key={feedback.feedback_id}
                            kind={classifyFeedback(feedback.feedback_name)}
                            label={feedback.feedback_name}
                            selected={evaluation.answer_id === feedback.feedback_id}
                            onClick={() => {
                                // Reclicar na resposta atual não desmarca.
                                if (evaluation.answer_id !== feedback.feedback_id) {
                                    onAnswer(item.insp_item_id, feedback.feedback_id);
                                }
                            }}
                        />
                    ))}
                    <ObservationToggle
                        open={obsOpen}
                        hasText={Boolean(evaluation.observation)}
                        onClick={() => setObsOpen((prev) => !prev)}
                    />
                </Box>
            </Box>
            {obsOpen && (
                <Box sx={{ mt: '12px' }}>
                    <TextField
                        fullWidth
                        autoFocus={!evaluation.observation}
                        label="Observação"
                        placeholder="Adicione uma observação sobre este item (opcional)"
                        value={evaluation.observation || ''}
                        onChange={(e) => onObservation(item.insp_item_id, e.target.value)}
                        variant="outlined"
                        multiline
                        rows={2}
                        sx={standaloneFieldSx}
                    />
                </Box>
            )}
        </Box>
    );
}

// `.check-group` — cabeçalho em gradiente da marca + linhas `.check-item`.
function CheckGroup({ group, items, feedbackOptions, evaluations, onAnswer, onObservation }) {
    const theme = useTheme();
    const answeredCount = items.filter((it) => evaluations[it.insp_item_id]?.answer_id).length;

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', overflow: 'hidden', mb: '14px', backgroundColor: 'background.paper', boxShadow: theme.customShadows.sm }}>
            <Box
                sx={{
                    p: '13px 16px', background: theme.palette.gradient.brand, color: '#fff', fontSize: 13, fontWeight: 800,
                    display: 'flex', alignItems: 'center', gap: '8px',
                }}
            >
                <ClipboardCheck size={15} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {group.group_name}
                </Box>
                <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, bgcolor: 'rgba(255,255,255,.2)', px: '9px', py: '3px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {answeredCount}/{items.length}
                </Box>
            </Box>

            {group.group_description && (
                <Box sx={{ p: '9px 16px', fontSize: 11.5, color: 'text.secondary', backgroundColor: 'background.subtle', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {group.group_description}
                </Box>
            )}

            {items.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: 'text.disabled', p: '22px 16px', textAlign: 'center' }}>
                    Nenhum item de inspeção cadastrado neste grupo
                </Typography>
            ) : items.map((item, idx) => (
                <CheckItemRow
                    key={item.insp_item_id}
                    item={item}
                    isLast={idx === items.length - 1}
                    feedbackOptions={feedbackOptions}
                    evaluation={evaluations[item.insp_item_id] || {}}
                    onAnswer={onAnswer}
                    onObservation={onObservation}
                />
            ))}
        </Box>
    );
}

// Skeleton do carregamento inicial.
const FillerSkeleton = () => (
    <Stack spacing={1.75}>
        <Skeleton variant="rounded" height={88} sx={{ borderRadius: '14px' }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '14px' }} />
        <Skeleton variant="rounded" height={160} sx={{ borderRadius: '14px' }} />
    </Stack>
);

export default function ChecklistFillerModal({ open, onClose, checklistId, serviceOrderId, onSuccess, onError, onMessage }) {
    const { hasPermission } = usePermissions();
    const canFillChecklist = hasPermission('serviceorder.checklist.fill');
    const isMobileShell = useIsMobileShell();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [saveProgress, setSaveProgress] = useState(0);
    
    // Service order data
    const [serviceOrder, setServiceOrder] = useState(null);
    
    // Checklist data
    const [groups, setGroups] = useState([]);
    
    // Form state - evaluations: { item_id: { answer_id: string, observation: string } }
    const [evaluations, setEvaluations] = useState({});

    const handleShowError = useCallback((errorMessage) => {
        if (onError) {
            onError(errorMessage);
        } else if (onMessage) {
            onMessage(errorMessage, 'error');
        }
    }, [onError, onMessage]);

    const handleShowSuccess = useCallback((successMessage) => {
        if (onMessage) {
            onMessage(successMessage, 'success');
        }
    }, [onMessage]);

    // Fetch service order data
    const fetchServiceOrder = async () => {
        if (!serviceOrderId) {
            handleShowError('ID da ordem de serviço não fornecido');
            return;
        }

        try {
            const response = await httpClient.get(`/orderservices/${serviceOrderId}`);
            
            if (response.status === 200 && response.data) {
                setServiceOrder(response.data);
            } else {
                handleShowError('Erro ao carregar dados da ordem de serviço');
            }
        } catch (error) {
            console.error('Error fetching service order:', error);
            handleShowError('Erro ao carregar dados da ordem de serviço');
        }
    };

    // Fetch checklist data
    const fetchChecklist = async () => {
        if (!checklistId) {
            handleShowError('ID do checklist não fornecido');
            return;
        }

        try {
            // Primeiro, verifica se já existe um checklist preenchido para esta ordem
            let filledChecklist = null;
            try {
                const filledResponse = await httpClient.get(`/checklist/service-order/${serviceOrderId}`);
                if (filledResponse.status === 200 && filledResponse.data) {
                    filledChecklist = filledResponse.data;
                    // Se o checklist preenchido corresponde ao checklistId atual, usa os dados preenchidos
                    if (String(filledChecklist.checklist_id) === String(checklistId)) {
                        // Carrega a estrutura do checklist
                        const structureResponse = await httpClient.get(`/checklist/${checklistId}/view`);
                        if (structureResponse.status === 200 && structureResponse.data) {
                            setGroups(structureResponse.data);
                            
                            // Preenche as avaliações com os dados já salvos
                            const filledEvaluations = {};
                            if (filledChecklist.evaluations && filledChecklist.evaluations.length > 0) {
                                filledChecklist.evaluations.forEach(evaluation => {
                                    filledEvaluations[evaluation.item_id] = {
                                        answer_id: evaluation.answer_id,
                                        observation: evaluation.observation || ''
                                    };
                                });
                            }
                            setEvaluations(filledEvaluations);
                            return;
                        }
                    }
                }
            } catch (error) {
                // Se não encontrar checklist preenchido, continua normalmente
                console.log('Nenhum checklist preenchido encontrado, carregando estrutura vazia');
            }

            // Se não encontrou checklist preenchido, carrega a estrutura vazia
            const response = await httpClient.get(`/checklist/${checklistId}/view`);
            
            if (response.status === 200 && response.data) {
                setGroups(response.data);
            } else {
                handleShowError('Erro ao carregar checklist');
            }
        } catch (error) {
            console.error('Error fetching checklist:', error);
            handleShowError('Erro ao carregar dados do checklist');
        }
    };

    // Get default feedback options if no feedbacks are found in groups
    const getDefaultFeedbackOptions = () => {
        return [
            { feedback_id: 'ok', feedback_name: 'OK', feedback_order: 1 },
            { feedback_id: 'nok', feedback_name: 'NOK', feedback_order: 2 },
            { feedback_id: 'manutencao', feedback_name: 'MANUTENÇÃO', feedback_order: 3 }
        ];
    };

    // Load initial data
    useEffect(() => {
        if (open && checklistId && serviceOrderId) {
            const loadData = async () => {
                setLoading(true);
                setEvaluations({});
                await Promise.all([
                    fetchServiceOrder(),
                    fetchChecklist()
                ]);
                setLoading(false);
            };
            loadData();
        }
    }, [open, checklistId, serviceOrderId]);

    // Handle answer change for an item
    const handleAnswerChange = (itemId, answerId) => {
        setEvaluations(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                answer_id: answerId
            }
        }));
    };

    // Handle observation change for an item
    const handleObservationChange = (itemId, observation) => {
        setEvaluations(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                observation: observation
            }
        }));
    };

    // Submit form
    // `photos` (só o shell mobile): fotos do veículo com marcações de avaria,
    // enviadas ao PDF do checklist como o fv-app faz (imagem já renderizada).
    const submitChecklist = async (photos = []) => {
        if (!serviceOrderId || !checklistId) {
            handleShowError('Dados incompletos para envio');
            return;
        }

        // Build evaluations array
        const evaluationsArray = Object.entries(evaluations)
            .filter(([itemId, data]) => data.answer_id) // Only include items with answers
            .map(([itemId, data]) => ({
                item_id: itemId,
                answer_id: data.answer_id,
                observation: data.observation || ''
            }));

        if (evaluationsArray.length === 0) {
            handleShowError('Por favor, preencha pelo menos um item do checklist');
            return;
        }

        const payload = {
            service_order_id: serviceOrderId,
            checklist_id: checklistId,
            evaluations: evaluationsArray
        };

        // Etapas ponderadas: envio do checklist 0–45, fotos 45–55, envio do PDF
        // 55–100 (o upload vai até 95; 100 só quando o servidor responde).
        // Nunca regride.
        const advance = (base, span, loaded, total) => {
            const fraction = total ? Math.min(loaded / total, 1) : 0;
            const next = Math.round(base + span * fraction);
            setSaveProgress((prev) => Math.max(prev, next));
        };

        try {
            setSaveProgress(0);
            setSubmitting(true);
            const response = await httpClient.post('/checklist/fill', payload, {
                onUploadProgress: (e) => advance(0, 40, e.loaded, e.total),
            });
            setSaveProgress((prev) => Math.max(prev, 45));

            if (response.status === 200 || response.status === 201) {
                // Shell mobile (igual ao fv-app): depois de salvar, gera o PDF do
                // checklist com as fotos já marcadas. Falha no PDF não desfaz o
                // checklist salvo — só avisa.
                // O POST do PDF é quem sobe o arquivo pro bucket, então roda em
                // qualquer shell (no desktop `photos` vem vazio).
                let pdfFailed = false;
                try {
                    let rendered = 0;
                    const images = await Promise.all(photos.map(async (photo) => {
                        const image = await renderPhotoBase64(photo);
                        rendered += 1;
                        advance(45, 10, rendered, photos.length);
                        return image;
                    }));
                    setSaveProgress((prev) => Math.max(prev, 55));
                    await httpClient.post('/checklist/service-order/pdf', {
                        serviceOrderID: serviceOrderId,
                        checklistID: checklistId,
                        images,
                    }, {
                        onUploadProgress: (e) => advance(55, 40, e.loaded, e.total),
                    });
                } catch (pdfError) {
                    console.error('Error generating checklist PDF:', pdfError);
                    pdfFailed = true;
                }
                if (pdfFailed) {
                    handleShowSuccess('Checklist salvo, mas não foi possível gerar o PDF com as fotos.');
                } else {
                    handleShowSuccess('Checklist preenchido com sucesso!');
                }
                setSaveProgress(100);
                if (onSuccess) {
                    onSuccess();
                }
                if (isMobileShell) {
                    onClose();
                } else {
                    // Mantém o progresso (100%) e os botões bloqueados até fechar,
                    // evitando novo clique em "Salvar" durante o atraso.
                    await new Promise((resolve) => setTimeout(resolve, 1500));
                    onClose();
                }
            } else {
                handleShowError(response.data?.message || 'Erro ao salvar checklist');
            }
        } catch (error) {
            console.error('Error submitting checklist:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar checklist';
            handleShowError(errorMessage);
        } finally {
            setSubmitting(false);
            setSaveProgress(0);
        }
    };

    // Format phone number
    const formatPhone = (phone) => {
        if (!phone) return '-';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (cleaned.length === 10) {
            return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    const handleSubmit = () => submitChecklist();

    // Shell mobile: preenchimento em tela cheia, no fluxo do fv-app (fotos com
    // marcação de avarias + itens). Desktop segue com o modal abaixo.
    if (isMobileShell) {
        if (!open) return null;
        return (
            <MobileChecklistFiller
                loading={loading}
                checklistId={checklistId}
                serviceOrder={serviceOrder?.order || serviceOrder}
                groups={groups}
                evaluations={evaluations}
                onAnswer={handleAnswerChange}
                onObservation={handleObservationChange}
                onSubmit={submitChecklist}
                onClose={onClose}
                submitting={submitting}
                saveProgress={saveProgress}
                canFill={canFillChecklist}
            />
        );
    }

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
            {/* header — ícone-badge + título + descrição + X */}
            <Box sx={{
                px: '22px',
                pt: '20px',
                pb: '14px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.25,
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                        mb: '10px',
                    }}>
                        <ClipboardCheck size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                        Preencher checklist
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        Preencha os itens de inspeção do checklist do veículo
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" disabled={submitting} aria-label="Fechar">
                    <X size={15} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* body — corpo rolável */}
            <Box sx={{ p: '18px 22px', maxHeight: '68vh', overflowY: 'auto' }}>
                {loading ? (
                    <FillerSkeleton />
                ) : (
                    <Box sx={{ width: '100%' }}>
                        {/* Ordem de serviço (cabeçalho cliente/veículo) */}
                        {serviceOrder && (
                            <Box sx={{ mb: '14px', p: '14px 16px', border: '1px solid', borderColor: 'divider', borderRadius: '14px', backgroundColor: 'background.subtle' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 800, mb: '12px' }}>
                                    Informações da ordem de serviço
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px 18px' }}>
                                    <RoField label="Número da ordem" value={serviceOrder.number || serviceOrder.id || '-'} />
                                    <RoField
                                        label="Cliente"
                                        value={serviceOrder.customer_first_name || serviceOrder.customer_name
                                            ? `${serviceOrder.customer_first_name || ''} ${serviceOrder.customer_last_name || ''}`.trim() || serviceOrder.customer_name
                                            : '-'}
                                    />
                                    <RoField
                                        label="Telefone/Celular"
                                        value={formatPhone(serviceOrder.customer_cellphone || serviceOrder.customer_phone || '-')}
                                    />
                                    {serviceOrder.vehicle_name && (
                                        <RoField
                                            label="Veículo"
                                            value={`${serviceOrder.vehicle_name}${serviceOrder.vehicle_plate ? ` - ${serviceOrder.vehicle_plate}` : ''}`}
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Grupos de inspeção e itens */}
                        {groups.length === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center', py: 4 }}>
                                <Box sx={{ width: 64, height: 64, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                    <ClipboardList size={26} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.secondary' }}>
                                    Nenhum grupo encontrado neste checklist
                                </Typography>
                            </Box>
                        ) : (
                            groups.map((group, groupIndex) => {
                                // Get feedback options for this group
                                let feedbackOptions = [];
                                if (group.group_feedback && group.group_feedback.length > 0) {
                                    feedbackOptions = group.group_feedback
                                        .filter(f => f.active !== false)
                                        .sort((a, b) => a.feedback_order - b.feedback_order);
                                } else {
                                    // Try to get feedbacks from other groups
                                    for (const otherGroup of groups) {
                                        if (otherGroup.group_feedback && otherGroup.group_feedback.length > 0) {
                                            feedbackOptions = otherGroup.group_feedback
                                                .filter(f => f.active !== false)
                                                .sort((a, b) => a.feedback_order - b.feedback_order);
                                            break;
                                        }
                                    }
                                    // If still no feedbacks found, use defaults
                                    if (feedbackOptions.length === 0) {
                                        feedbackOptions = getDefaultFeedbackOptions();
                                    }
                                }
                                
                                const inspectionItems = group.group_inspection_items
                                    ? group.group_inspection_items
                                        .filter(i => i.active !== false)
                                        .sort((a, b) => a.insp_item_order - b.insp_item_order)
                                    : [];

                                return (
                                    <CheckGroup
                                        key={group.group_id}
                                        group={group}
                                        items={inspectionItems}
                                        feedbackOptions={feedbackOptions}
                                        evaluations={evaluations}
                                        onAnswer={handleAnswerChange}
                                        onObservation={handleObservationChange}
                                    />
                                );
                            })
                        )}
                    </Box>
                )}
            </Box>

            {/* footer — ações à direita */}
            <Box sx={{
                p: '16px 22px 20px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1.25,
                justifyContent: 'flex-end',
            }}>
                <Button variant="outlined" onClick={onClose} disabled={submitting}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <Check size={15} strokeWidth={2.4} />}
                    onClick={handleSubmit}
                    disabled={submitting || !canFillChecklist || Object.keys(evaluations).length === 0}
                >
                    {submitting ? 'Salvando...' : 'Salvar checklist'}
                </Button>
            </Box>

            <SaveProgressDialog open={submitting} progress={saveProgress} />
        </Dialog>
    );
}

